// src/services/api_client.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";

let refreshTimer = null;

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:3000/api",
  headers: { "Content-Type": "application/json" },
});

/* small helpers */
function generateUUID() {
  // Fallback for browsers that don't support crypto.randomUUID()
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Polyfill for older browsers/Safari
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getDeviceId() {
  let deviceId = sessionStorage.getItem("device_id");
  if (!deviceId) {
    // first access in this tab -> create it
    deviceId = generateUUID();
    sessionStorage.setItem("device_id", deviceId);
  }
  return deviceId;
}

// Install headers lazily (first request) instead of at import time
apiClient.interceptors.request.use((config) => {
  // set device id if missing
  if (!config.headers["X-Device-Id"]) {
    const did = getDeviceId();
    config.headers["X-Device-Id"] = did;
  }
  // attach access token unless skipped
  if (config.skipAuth !== true) {
    const token = sessionStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// We will inject these from main.js to avoid store/router imports here
let externalForceLogout = null;
export function setForceLogout(fn) {
  externalForceLogout = fn;
}

export function scheduleRefresh() {
  try {
    const accessToken = sessionStorage.getItem("authToken");
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!accessToken || !refreshToken) return;

    let exp;
    try {
      ({ exp } = jwtDecode(accessToken));
    } catch (err) {
      // malformed/expired token -> logout
      externalForceLogout?.();
      return;
    }

    const now = Date.now() / 1000;
    const buffer = 90;
    const delay = Math.max((exp - now - buffer) * 1000, 0);

    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        const resp = await apiClient.post("/auth/refresh", null, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("refreshToken")}`,
          },
          skipAuth: true,
        });
        const newAt = resp.data.access_token;
        sessionStorage.setItem("authToken", newAt);
        scheduleRefresh();
      } catch (e) {
        console.error("[api] refresh failed", e);
        externalForceLogout?.();
      }
    }, delay);
  } catch (e) {
    console.error("[api] scheduleRefresh error", e);
  }
}

export function stopRefreshScheduler() {
  clearTimeout(refreshTimer);
  refreshTimer = null;
}

// function forceLogout () {
//     const session = useSessionStore();
//     session.logout();
//     router.push({ path: '/login', query: { error: 'session-expired' }});
// }

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;

    // 1st 401 -> try refresh
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const rt = sessionStorage.getItem("refreshToken");
      if (!rt) {
        externalForceLogout?.();
        return Promise.reject(err);
      }

      try {
        const { data } = await apiClient.post("/auth/refresh", null, {
          headers: {
            Authorization: `Bearer ${rt}`,
          },
          skipAuth: true,
        });
        const newAT = data.access_token;
        console.log(
          `api_client.js, apiClient.interceptors.response, New auth token was recieved`,
        );
        sessionStorage.setItem("authToken", newAT);
        scheduleRefresh();
        console.log(
          `api_client.js, apiClient.interceptors.response, Started scheduleRefresh`,
        );
        original.headers.Authorization = `Bearer ${newAT}`;
        original.skipAuth = true;
        return apiClient(original); // retry once with fresh auth token
      } catch (refreshErr) {
        /* refresh failed (token revoked or user re‑logged elsewhere) */
        externalForceLogout?.();
        return Promise.reject(refreshErr);
      }
    }

    // 2nd 401 (after retry) -> definitely invalid
    if (err.response?.status === 401 && original._retry) {
      externalForceLogout?.();
    }

    return Promise.reject(err);
  },
);

export { apiClient, getDeviceId };
